import { useState, type ReactNode } from 'react';
import { classic42 } from '@risk/maps';
import type { ClientState, GameClient } from '../net/client.js';

/**
 * Corrections.
 *
 * Anyone can raise one, about anything, at any point in the game -- which is
 * the whole reason the log is append-only. It takes a second player to apply
 * one, so the app never lets one person quietly rewrite the board; the host
 * can confirm alone, for when the only other phone has died.
 */
export function PendingCorrections({
  state,
  client,
}: {
  state: ClientState;
  client: GameClient;
}): ReactNode {
  if (state.proposals.length === 0) return null;

  return (
    <>
      {state.proposals.map((proposal) => {
        const raisedBy = state.game.seats.find((seat) => seat.id === proposal.by);
        const mine = proposal.by === state.seatId;
        const me = state.game.seats.find((seat) => seat.id === state.seatId);
        const canConfirm = !mine || me?.isHost === true;

        return (
          <div className="notice" key={proposal.id} style={{ display: 'block' }}>
            <div><strong>{raisedBy?.name ?? 'Someone'} says the board is wrong</strong></div>
            <div className="small">{proposal.summary}</div>
            {proposal.rationale && <div className="small muted">“{proposal.rationale}”</div>}
            <div className="row" style={{ marginTop: 10 }}>
              <button
                className="grow primary small"
                disabled={!canConfirm}
                onClick={() => client.confirmCorrection(proposal.id)}
              >
                {canConfirm ? 'Confirm' : 'Waiting for someone else'}
              </button>
              <button className="small" onClick={() => client.rejectCorrection(proposal.id)}>
                Dismiss
              </button>
            </div>
          </div>
        );
      })}
    </>
  );
}

/** Raise a correction about any territory, on anyone's turn. */
export function RaiseCorrection({
  state,
  client,
}: {
  state: ClientState;
  client: GameClient;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const [territory, setTerritory] = useState('');
  const [armies, setArmies] = useState(1);
  const [owner, setOwner] = useState('');
  const [why, setWhy] = useState('');

  if (!open) {
    return (
      <button className="ghost small" style={{ width: '100%' }} onClick={() => setOpen(true)}>
        Something on the board is wrong
      </button>
    );
  }

  const current = territory ? state.game.territories[territory] : undefined;

  return (
    <div className="card">
      <h2>Correct the board</h2>
      <p className="small muted" style={{ marginTop: 0 }}>
        Another player has to agree before this is applied.
      </p>
      <div className="stack">
        <select
          value={territory}
          onChange={(e) => {
            setTerritory(e.target.value);
            const t = state.game.territories[e.target.value];
            if (t) { setArmies(t.armies); setOwner(t.owner ?? ''); }
          }}
        >
          <option value="">Which territory?</option>
          {classic42.territories.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>

        {current && (
          <>
            <select value={owner} onChange={(e) => setOwner(e.target.value)}>
              {state.game.seats.map((seat) => (
                <option key={seat.id} value={seat.id}>{seat.name}</option>
              ))}
            </select>
            <label className="small muted" htmlFor="armies">Armies actually on the board</label>
            <input
              id="armies"
              type="number"
              min={0}
              value={armies}
              onChange={(e) => setArmies(Math.max(0, Number(e.target.value)))}
            />
            <input
              placeholder="Why? (optional)"
              value={why}
              maxLength={120}
              onChange={(e) => setWhy(e.target.value)}
            />
          </>
        )}

        <div className="row">
          <button className="grow" onClick={() => setOpen(false)}>Cancel</button>
          <button
            className="grow primary"
            disabled={!territory || !owner}
            onClick={() => {
              client.proposeCorrection(territory, owner, armies, why);
              setOpen(false);
              setTerritory('');
              setWhy('');
            }}
          >
            Raise it
          </button>
        </div>
      </div>
    </div>
  );
}
